import { supabase } from '../supabaseClient.js';

/**
 * Add sample categories to populate the database
 * This creates a hierarchical category structure for PropShare
 */
const addSampleCategories = async () => {
  try {
    console.log('🌱 Starting to add sample categories...');

    // Step 1: Add root categories
    const rootCategories = [
      {
        name: 'Furniture',
        description: 'All furniture items including seating, tables, storage, and decorative pieces',
        sort_order: 1
      },
      {
        name: 'Costumes & Textiles',
        description: 'Clothing, costumes, fabrics, and textile-based props',
        sort_order: 2
      },
      {
        name: 'Hand Props',
        description: 'Small props that actors handle directly during performances',
        sort_order: 3
      },
      {
        name: 'Set Pieces',
        description: 'Large structural elements and scenic pieces',
        sort_order: 4
      },
      {
        name: 'Lighting & Effects',
        description: 'Lighting equipment, special effects, and technical equipment',
        sort_order: 5
      },
      {
        name: 'Audio & Electronics',
        description: 'Sound equipment, electronics, and audio props',
        sort_order: 6
      },
      {
        name: 'Vehicles & Transport',
        description: 'Cars, bikes, carts, and transportation-related props',
        sort_order: 7
      },
      {
        name: 'Weapons & Combat',
        description: 'Stage weapons, armor, and combat-related props (all stage-safe)',
        sort_order: 8
      }
    ];

    console.log('📁 Adding root categories...');
    const { data: rootData, error: rootError } = await supabase
      .from('categories')
      .insert(rootCategories)
      .select();

    if (rootError) throw rootError;
    console.log('✅ Root categories added:', rootData.length);

    // Step 2: Add subcategories
    // Get the root category IDs for reference
    const furnitureId = rootData.find(c => c.name === 'Furniture')?.id;
    const costumesId = rootData.find(c => c.name === 'Costumes & Textiles')?.id;
    const handPropsId = rootData.find(c => c.name === 'Hand Props')?.id;
    const setPiecesId = rootData.find(c => c.name === 'Set Pieces')?.id;
    const lightingId = rootData.find(c => c.name === 'Lighting & Effects')?.id;
    const audioId = rootData.find(c => c.name === 'Audio & Electronics')?.id;
    const vehiclesId = rootData.find(c => c.name === 'Vehicles & Transport')?.id;
    const weaponsId = rootData.find(c => c.name === 'Weapons & Combat')?.id;

    const subcategories = [
      // Furniture subcategories
      { name: 'Seating', description: 'Chairs, sofas, benches, stools', parent_id: furnitureId, sort_order: 1 },
      { name: 'Tables', description: 'Dining tables, desks, coffee tables', parent_id: furnitureId, sort_order: 2 },
      { name: 'Storage', description: 'Cabinets, dressers, shelving', parent_id: furnitureId, sort_order: 3 },
      { name: 'Beds', description: 'Beds, mattresses, bedroom furniture', parent_id: furnitureId, sort_order: 4 },
      { name: 'Decorative', description: 'Mirrors, artwork, decorative furniture', parent_id: furnitureId, sort_order: 5 },

      // Costumes & Textiles subcategories
      { name: 'Period Costumes', description: 'Historical and period-specific clothing', parent_id: costumesId, sort_order: 1 },
      { name: 'Modern Clothing', description: 'Contemporary clothing and accessories', parent_id: costumesId, sort_order: 2 },
      { name: 'Accessories', description: 'Hats, jewelry, bags, scarves', parent_id: costumesId, sort_order: 3 },
      { name: 'Fabrics & Draping', description: 'Curtains, fabric swatches, draping materials', parent_id: costumesId, sort_order: 4 },
      { name: 'Shoes', description: 'Period and contemporary footwear', parent_id: costumesId, sort_order: 5 },

      // Hand Props subcategories
      { name: 'Kitchen & Dining', description: 'Dishes, utensils, cookware, food props', parent_id: handPropsId, sort_order: 1 },
      { name: 'Books & Papers', description: 'Books, newspapers, documents, writing materials', parent_id: handPropsId, sort_order: 2 },
      { name: 'Tools & Instruments', description: 'Musical instruments, hand tools, scientific instruments', parent_id: handPropsId, sort_order: 3 },
      { name: 'Personal Items', description: 'Suitcases, purses, personal effects', parent_id: handPropsId, sort_order: 4 },
      { name: 'Decorative Objects', description: 'Vases, candlesticks, art pieces', parent_id: handPropsId, sort_order: 5 },

      // Set Pieces subcategories
      { name: 'Architectural', description: 'Columns, arches, walls, doors, windows', parent_id: setPiecesId, sort_order: 1 },
      { name: 'Backdrops', description: 'Painted backdrops, cycloramas, sky drops', parent_id: setPiecesId, sort_order: 2 },
      { name: 'Platforms', description: 'Risers, platforms, stairs, levels', parent_id: setPiecesId, sort_order: 3 },
      { name: 'Nature Elements', description: 'Trees, rocks, water features, landscape pieces', parent_id: setPiecesId, sort_order: 4 },

      // Lighting & Effects subcategories
      { name: 'Stage Lighting', description: 'Theatrical lighting fixtures and equipment', parent_id: lightingId, sort_order: 1 },
      { name: 'Practical Lights', description: 'Lamps, candles, chandeliers for on-stage use', parent_id: lightingId, sort_order: 2 },
      { name: 'Special Effects', description: 'Fog machines, projectors, lighting effects', parent_id: lightingId, sort_order: 3 },

      // Audio & Electronics subcategories
      { name: 'Sound Equipment', description: 'Microphones, speakers, sound boards', parent_id: audioId, sort_order: 1 },
      { name: 'Electronics Props', description: 'Fake phones, radios, electronic prop pieces', parent_id: audioId, sort_order: 2 },

      // Vehicles & Transport subcategories
      { name: 'Period Vehicles', description: 'Historical carts, carriages, vintage items', parent_id: vehiclesId, sort_order: 1 },
      { name: 'Modern Transport', description: 'Bikes, scooters, modern vehicle props', parent_id: vehiclesId, sort_order: 2 },

      // Weapons & Combat subcategories
      { name: 'Swords & Blades', description: 'Stage swords, knives, blade weapons', parent_id: weaponsId, sort_order: 1 },
      { name: 'Armor & Shields', description: 'Stage armor, shields, protective gear', parent_id: weaponsId, sort_order: 2 },
      { name: 'Projectile Props', description: 'Bows, crossbows, guns (all stage-safe)', parent_id: weaponsId, sort_order: 3 }
    ];

    console.log('📂 Adding subcategories...');
    const { data: subData, error: subError } = await supabase
      .from('categories')
      .insert(subcategories)
      .select();

    if (subError) throw subError;
    console.log('✅ Subcategories added:', subData.length);

    // Step 3: Add some third-level categories for popular items
    const seatingId = subData.find(c => c.name === 'Seating')?.id;
    const kitchenId = subData.find(c => c.name === 'Kitchen & Dining')?.id;
    const periodCostumesId = subData.find(c => c.name === 'Period Costumes')?.id;

    const thirdLevelCategories = [
      // Seating types
      { name: 'Chairs', description: 'Individual chairs of all styles', parent_id: seatingId, sort_order: 1 },
      { name: 'Sofas & Couches', description: 'Multi-seat furniture', parent_id: seatingId, sort_order: 2 },
      { name: 'Benches', description: 'Benches and long seating', parent_id: seatingId, sort_order: 3 },
      { name: 'Stools', description: 'Bar stools, ottomans, footstools', parent_id: seatingId, sort_order: 4 },

      // Kitchen specifics
      { name: 'Dishes & Plates', description: 'Plates, bowls, serving dishes', parent_id: kitchenId, sort_order: 1 },
      { name: 'Glassware', description: 'Glasses, cups, drinkware', parent_id: kitchenId, sort_order: 2 },
      { name: 'Cookware', description: 'Pots, pans, cooking utensils', parent_id: kitchenId, sort_order: 3 },

      // Period costume eras
      { name: 'Victorian Era', description: '1837-1901 Victorian period clothing', parent_id: periodCostumesId, sort_order: 1 },
      { name: 'Medieval', description: 'Medieval and Renaissance period costumes', parent_id: periodCostumesId, sort_order: 2 },
      { name: '1920s-1950s', description: 'Early to mid 20th century clothing', parent_id: periodCostumesId, sort_order: 3 }
    ];

    console.log('📝 Adding third-level categories...');
    const { data: thirdData, error: thirdError } = await supabase
      .from('categories')
      .insert(thirdLevelCategories)
      .select();

    if (thirdError) throw thirdError;
    console.log('✅ Third-level categories added:', thirdData.length);

    console.log('🎉 Sample categories added successfully!');
    console.log(`Total categories created: ${rootData.length + subData.length + thirdData.length}`);

    return {
      success: true,
      totalCreated: rootData.length + subData.length + thirdData.length,
      rootCategories: rootData.length,
      subcategories: subData.length,
      thirdLevel: thirdData.length
    };

  } catch (error) {
    console.error('❌ Error adding sample categories:', error);
    throw error;
  }
};

// Export for use in other components or run directly
export default addSampleCategories;

// Uncomment to run immediately when imported
// addSampleCategories().then(result => {
//   console.log('✅ Categories setup complete:', result);
// }).catch(error => {
//   console.error('❌ Categories setup failed:', error);
// });
